/** Economy & server: faucets vs sinks, float/liability, and runtime health. */
import { el } from '../../dom';
import { adminApi, type AdminEconomy, type AdminServerInfo } from '../../game-api';
import { fmtMoney, fmtNum, fmtUptime, sumAmounts } from './format';
import { emptyBox, grid, loadInto, panel, pill, statCard, table } from './ui';

export function renderEconomy(host: HTMLElement): () => void {
  const economyHost = el('div');
  const serverHost = el('div');
  host.appendChild(el('div', 'flex flex-col gap-4', {}, economyHost, serverHost));

  const stopEconomy = loadInto(economyHost, () => adminApi.economy(), (e) => economyView(e), "Couldn't load economy figures.");
  const stopServer = loadInto(serverHost, () => adminApi.server(), (s) => serverView(s), "Couldn't load server data.");
  return () => { stopEconomy(); stopServer(); };
}

function economyView(e: AdminEconomy): HTMLElement {
  const faucets = sumAmounts(e.faucets);
  const sinks = sumAmounts(e.sinks);
  const cards = grid(190);
  cards.append(
    statCard('Faucets (in)', fmtNum(faucets)),
    statCard('Sinks (out)', fmtNum(sinks)),
    statCard('Net', fmtNum(faucets - sinks), faucets - sinks >= 0 ? 'inflationary' : 'deflationary'),
    statCard('Float', fmtNum(e.float)),
    statCard('Deposits', fmtMoney(e.deposits)),
    statCard('Commission', fmtMoney(e.commission)),
    statCard('Withdrawal liability', fmtMoney(e.withdrawalLiability)),
    statCard('Circulating', fmtNum(e.circulatingGold), `${fmtNum(e.circulatingShrooms)} shrooms`),
  );

  const breakdown = el('div', 'grid gap-4', { style: 'grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));' },
    panel('Faucets', e.faucets?.length
      ? table(['Source', 'Amount'], e.faucets.map((r) => [r.source, fmtNum(r.amount)]))
      : emptyBox('No faucet data.')),
    panel('Sinks', e.sinks?.length
      ? table(['Source', 'Amount'], e.sinks.map((r) => [r.source, fmtNum(r.amount)]))
      : emptyBox('No sink data.')));

  return el('div', 'flex flex-col gap-4', {}, cards, breakdown);
}

function serverView(s: AdminServerInfo): HTMLElement {
  const cards = grid(190);
  cards.append(
    statCard('Uptime', fmtUptime(s.uptimeSec)),
    statCard('Environment', s.env || '—', s.version ? `v${s.version}` : undefined),
    statCard('Database', s.db?.ok ? 'online' : 'offline', s.db?.latencyMs != null ? `${s.db.latencyMs} ms` : undefined),
    statCard('Redis', s.redis?.ok ? 'online' : 'offline', s.redis?.latencyMs != null ? `${s.redis.latencyMs} ms` : undefined),
  );
  const queues = panel('Queues', s.queues?.length
    ? table(['Queue', 'Waiting', 'Active', 'Failed'], s.queues.map((q) => [
      q.name, fmtNum(q.waiting), fmtNum(q.active),
      q.failed > 0 ? pill(fmtNum(q.failed), 'bad') : fmtNum(q.failed),
    ]))
    : emptyBox('No queues registered.'));
  return el('div', 'flex flex-col gap-4', {}, panel('Server', cards), queues);
}
